// Gates.js - Interface de Portões Smart Home v4.1
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, DoorOpen, RefreshCw, Wifi, WifiOff,
  AlertCircle, Clock, History, Settings
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Gates.css';

// Todos os endpoints passaram a exigir autenticacao (antes alguns respondiam
// a qualquer pessoa, o que era o verdadeiro problema).
function authHeaders() {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


function Gates({ apiUrl, onBack }) {
  const { t, i18n } = useTranslation();
  const loc = () => (i18n.language === 'en' ? 'en-GB' : 'pt-PT');
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [triggeringGate, setTriggeringGate] = useState(null);
  const [confirmGate, setConfirmGate] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Carregar portões
  useEffect(() => {
    loadGates();
  }, []);

  const loadGates = async () => {
    try {
      setError('');
      const response = await fetch(`${apiUrl}/gates/devices`, { headers: authHeaders() });
      const data = await response.json();
      setGates(data.devices || []);
      console.log(`🚪 ${data.devices?.length || 0} portões carregados`);
    } catch (err) {
      console.error('Erro ao carregar portões:', err);
      setError(t('portoes.erroCarregar'));
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await fetch(`${apiUrl}/gates/history?limit=20`, { headers: authHeaders() });
      const data = await response.json();
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    }
  };

  const handleTrigger = async (gate) => {
    // Mostrar confirmação
    setConfirmGate(gate);
  };

  const confirmTrigger = async () => {
    if (!confirmGate) return;

    const gate = confirmGate;
    setConfirmGate(null);
    setTriggeringGate(gate.id);
    setError('');

    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(`${apiUrl}/gates/${gate.id}/trigger`, {
        method: 'POST',
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}
      });

      const data = await response.json();

      if (data.success) {
        console.log(`✅ ${gate.name} acionado`);
        // Feedback visual temporário
        setTimeout(() => {
          setTriggeringGate(null);
        }, 1500);
      } else {
        setError(data.detail || t('portoes.erroAcionar'));
        setTriggeringGate(null);
      }
    } catch (err) {
      console.error('Erro ao acionar portão:', err);
      setError(t('portoes.erroAcionar'));
      setTriggeringGate(null);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString(loc());
  };

  if (loading) {
    return (
      <div className="gates-container">
        <div className="gates-loading">
          <DoorOpen size={48} className="spin" />
          <p>{t('portoes.aCarregar')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gates-container">
      <div className="gates-content">
        {/* Header */}
        <div className="gates-header">
          <button onClick={onBack} className="btn-back">
            <ArrowLeft size={24} />
          </button>
          <div className="header-info">
            <div className="header-title">
              <DoorOpen size={32} />
              <h1>{t('portoes.titulo')}</h1>
            </div>
            <span className="header-subtitle">
              {gates.filter(g => g.online).length} de {gates.length} online
            </span>
          </div>
          <div className="header-actions">
            <button onClick={loadHistory} className="btn-history" title={t('portoes.historico')}>
              <History size={20} />
            </button>
            <button onClick={loadGates} className="btn-refresh" title={t('comum.atualizar')}>
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Confirmação Modal */}
        {confirmGate && (
          <div className="modal-overlay" onClick={() => setConfirmGate(null)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-icon">
                <DoorOpen size={48} />
              </div>
              <h3>{t('portoes.confirmarTitulo')}</h3>
              <p>{t('portoes.confirmarTexto')} <strong>{confirmGate.name}</strong>?</p>
              <div className="confirm-actions">
                <button onClick={() => setConfirmGate(null)} className="btn-cancel">
                  {t('comum.cancelar')}
                </button>
                <button onClick={confirmTrigger} className="btn-confirm">
                  <DoorOpen size={20} />
                  {t('portoes.acionar')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Histórico Modal */}
        {showHistory && (
          <div className="modal-overlay" onClick={() => setShowHistory(false)}>
            <div className="history-modal" onClick={e => e.stopPropagation()}>
              <div className="history-header">
                <h3><History size={24} /> {t('portoes.historicoTitulo')}</h3>
                <button onClick={() => setShowHistory(false)} className="modal-close">×</button>
              </div>
              <div className="history-list">
                {history.length === 0 ? (
                  <p className="history-empty">{t('portoes.semRegistos')}</p>
                ) : (
                  history.map((entry, index) => (
                    <div key={index} className="history-item">
                      <DoorOpen size={18} />
                      <div className="history-info">
                        <span className="history-name">{entry.gate_name}</span>
                        <span className="history-time">
                          <Clock size={14} />
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                      <span className="history-source">{entry.source}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Gates Grid */}
        {gates.length === 0 ? (
          <div className="empty-state">
            <DoorOpen size={64} />
            <h3>{t('portoes.semPortoes')}</h3>
            <p>{t('portoes.semPortoesDica')}</p>
          </div>
        ) : (
          <div className="gates-grid">
            {gates.map(gate => (
              <div 
                key={gate.id} 
                className={`gate-card ${gate.online ? 'online' : 'offline'} ${triggeringGate === gate.id ? 'triggering' : ''}`}
              >
                {/* Status Badge */}
                <div className="gate-status">
                  {gate.online ? (
                    <span className="status-badge online">
                      <Wifi size={14} /> Online
                    </span>
                  ) : (
                    <span className="status-badge offline">
                      <WifiOff size={14} /> Offline
                    </span>
                  )}
                </div>

                {/* Gate Info */}
                <div className="gate-info">
                  <div className="gate-icon">
                    <DoorOpen size={48} />
                  </div>
                  <h2>{gate.name}</h2>
                  <p className="gate-ip">{gate.ip}</p>
                </div>

                {/* Trigger Button */}
                <button
                  onClick={() => handleTrigger(gate)}
                  disabled={!gate.online || triggeringGate === gate.id}
                  className={`btn-trigger ${triggeringGate === gate.id ? 'active' : ''}`}
                >
                  {triggeringGate === gate.id ? (
                    <>
                      <RefreshCw size={24} className="spin" />
                      A acionar...
                    </>
                  ) : (
                    <>
                      <DoorOpen size={24} />
                      Abrir / Fechar
                    </>
                  )}
                </button>

                {/* Pulse Duration Info */}
                <div className="gate-meta">
                  <Clock size={14} />
                  <span>Impulso: {gate.pulse_duration || 500}ms</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Gates;
